/**
 * The letter type as the chosen engagement basis rewrites it.
 *
 * Split out of `pages/attest/LetterGenerator.tsx` under the 350-line rule in
 * `docs/FILE_SIZE_POLICY.md` (ATTEST-UX-2 Bundle 2). Behaviour is unchanged;
 * only its address is.
 *
 * ATTEST-ENGAGEMENT-BASIS-2: the server already publishes each basis as a
 * complete summary, so the later steps read the right allowed assertions,
 * required inputs and independence posture with no second fetch and no logic of
 * their own.
 *
 * **This is a display merge only.** The basis the letter is actually written on
 * is decided server side from `engagement_basis` on the request, never from the
 * object this returns. A CPA cannot widen what the letter may say by winning an
 * argument with this function.
 */
import { useMemo } from 'react'
import type { LetterTypeSummary } from '@/components/attest/api'

/**
 * @param baseType   the type as the registry publishes it, or null
 * @param selectedBasis  the CPA's pick; '' means "the type's declared default"
 */
export function useBasisMergedType(
  baseType: LetterTypeSummary | null,
  selectedBasis: string,
): LetterTypeSummary | null {
  return useMemo(() => {
    const choice = baseType?.engagement_basis
    if (!baseType || !choice) return baseType
    const option = choice.options.find(
      o => o.key === (selectedBasis || choice.default),
    )
    if (!option) return baseType
    return {
      ...baseType,
      basis_key: option.key,
      display_name: option.display_name,
      requestor_context: option.requestor_context,
      engagement_type: option.engagement_type,
      allowed_assertions: option.allowed_assertions,
      // Re-taken from the option deliberately. It is the same list either way —
      // that is the point of the feature — and taking it from the option is what
      // would make a divergence visible here rather than hidden.
      denied_assertions: option.denied_assertions,
      required_inputs: option.required_inputs,
      // Step 3's checklist renders from this. A narrowing basis restates the
      // block, so taking it from the base type here would show the CPA rows for
      // assertions their chosen basis does not allow.
      required_documents: option.required_documents,
      independence_required: option.independence_required,
      regulatory_basis: option.regulatory_basis,
    }
  }, [baseType, selectedBasis])
}
